import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useData } from '../../hooks/useData';
import { useVendorCreate } from '../../hooks/useVendorCreate';
import { useApp } from '../../contexts/AppContext';
import { TABLES } from '../../constants/supabaseConstants';
import { supabase } from '../../config/supabase';
import { formatPhoneNumber, formatUrl } from '../../utils/formatters';
import modalStyles from '../../styles/modals.module.css';
import { PencilSimple, Plus, User, Phone, Envelope, Globe, Note } from '@phosphor-icons/react';

const DEFAULT_VENDOR_VALUES = {
  name: '',
  vendor_type_id: '',
  website: '',
  contact_person: '',
  contact_email: '',
  contact_phone: '',
  notes: '',
};

// Screen 1: Select/View vendor
const SelectVendorScreen = ({
  masterVendors,
  selectedVendorId,
  setSelectedVendorId,
  onEdit,
  onAddNew,
  onConfirm,
  onClose,
}) => {
  const selectedVendor = masterVendors?.find(v => v.id === selectedVendorId);

  return (
    <>
      {/* Header */}
      <div className={modalStyles.header}>
        <h2 className={modalStyles.headerTitle}>בחירת ספק</h2>
        <p className={modalStyles.headerSubtitle}>בחר ספק קיים או הוסף ספק חדש</p>
      </div>

      {/* Content */}
      <div className={modalStyles.content}>
        <div className={modalStyles.formGrid}>
          {/* Vendor Selection Dropdown */}
          <div className={modalStyles.field}>
            <label className={modalStyles.fieldLabel}>בחר ספק</label>
            <select
              value={selectedVendorId || ''}
              onChange={e => setSelectedVendorId(e.target.value || null)}
              className={modalStyles.fieldSelect}
            >
              <option value="">-- בחר ספק --</option>
              {masterVendors?.map(vendor => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </option>
              ))}
            </select>
          </div>

          {/* Selected Vendor Info (read-only) */}
          {selectedVendor && (
            <div
              style={{
                background: '#f8fafc',
                borderRadius: '12px',
                padding: '16px',
                marginTop: '8px',
                border: '1px solid #e2e8f0',
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  fontSize: '16px',
                  marginBottom: '12px',
                  color: '#1e293b',
                }}
              >
                {selectedVendor.name}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {selectedVendor.contact_person && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#64748b',
                      fontSize: '14px',
                    }}
                  >
                    <User size={16} />
                    <span>{selectedVendor.contact_person}</span>
                  </div>
                )}
                {selectedVendor.contact_phone && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#64748b',
                      fontSize: '14px',
                    }}
                  >
                    <Phone size={16} />
                    <span>{selectedVendor.contact_phone}</span>
                  </div>
                )}
                {selectedVendor.contact_email && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#64748b',
                      fontSize: '14px',
                    }}
                  >
                    <Envelope size={16} />
                    <span>{selectedVendor.contact_email}</span>
                  </div>
                )}
                {selectedVendor.website && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#64748b',
                      fontSize: '14px',
                    }}
                  >
                    <Globe size={16} />
                    <span>{selectedVendor.website}</span>
                  </div>
                )}
                {selectedVendor.notes && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      color: '#64748b',
                      fontSize: '14px',
                      marginTop: '4px',
                    }}
                  >
                    <Note size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{selectedVendor.notes}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button
              type="button"
              onClick={onEdit}
              disabled={!selectedVendorId}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '12px 16px',
                background: selectedVendorId ? '#f1f5f9' : '#f8fafc',
                color: selectedVendorId ? '#475569' : '#94a3b8',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: selectedVendorId ? 'pointer' : 'not-allowed',
                transition: 'all 0.15s ease',
              }}
            >
              <PencilSimple size={18} />
              ערוך ספק
            </button>
            <button
              type="button"
              onClick={onAddNew}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '12px 16px',
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <Plus size={18} />
              הוסף ספק חדש
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={modalStyles.footer}>
        <button type="button" className={modalStyles.cancelButton} onClick={onClose}>
          ביטול
        </button>
        <button
          type="button"
          className={modalStyles.primaryButton}
          onClick={() => {
            console.log('[SelectVendorScreen] Save clicked, selectedVendorId:', selectedVendorId);
            onConfirm();
          }}
          disabled={!selectedVendorId}
        >
          שמור
        </button>
      </div>
    </>
  );
};

// Screen 2: Edit/Create vendor form
const EditVendorScreen = ({
  formData,
  setFormData,
  errors,
  setErrors,
  isEditing,
  onSave,
  onCancel,
  isSaving,
  vendorTypesGrouped,
}) => {
  const handleChange = e => {
    const { name, value } = e.target;

    // Format special fields as user types
    if (name === 'contact_phone') {
      setFormData(prev => ({ ...prev, [name]: formatPhoneNumber(value) }));
    } else if (name === 'website') {
      setFormData(prev => ({ ...prev, [name]: formatUrl(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  return (
    <>
      {/* Header */}
      <div className={modalStyles.header}>
        <h2 className={modalStyles.headerTitle}>
          {isEditing ? 'עריכת פרטי ספק' : 'הוספת ספק חדש'}
        </h2>
        <p className={modalStyles.headerSubtitle}>
          {isEditing ? 'עדכן את פרטי הספק' : 'הזן את פרטי הספק החדש'}
        </p>
      </div>

      {/* Content */}
      <div className={modalStyles.content}>
        {errors.submit && <div className={modalStyles.error}>{errors.submit}</div>}

        <div className={modalStyles.formGrid}>
          {/* Vendor Name - Required */}
          <div className={modalStyles.field}>
            <label className={modalStyles.fieldLabel}>
              <span className={modalStyles.fieldRequired}>*</span>
              שם ספק
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`${modalStyles.fieldInput} ${errors.name ? modalStyles.error : ''}`}
              placeholder="הזן שם ספק"
            />
            {errors.name && <div className={modalStyles.fieldError}>{errors.name}</div>}
          </div>

          {/* Vendor Type - Required */}
          <div className={modalStyles.field}>
            <label className={modalStyles.fieldLabel}>
              <span className={modalStyles.fieldRequired}>*</span>
              סוג ספק
            </label>
            <select
              name="vendor_type_id"
              value={formData.vendor_type_id || ''}
              onChange={handleChange}
              className={`${modalStyles.fieldSelect} ${errors.vendor_type_id ? modalStyles.error : ''}`}
            >
              <option value="">-- בחר סוג ספק --</option>
              {vendorTypesGrouped?.map(category => (
                <optgroup key={category.category} label={category.category}>
                  {category.types.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {errors.vendor_type_id && (
              <div className={modalStyles.fieldError}>{errors.vendor_type_id}</div>
            )}
          </div>

          {/* Contact Person - Optional */}
          <div className={modalStyles.field}>
            <label className={modalStyles.fieldLabel}>
              איש קשר
              <span className={modalStyles.fieldOptional}>(אופציונלי)</span>
            </label>
            <input
              type="text"
              name="contact_person"
              value={formData.contact_person}
              onChange={handleChange}
              className={modalStyles.fieldInput}
              placeholder="שם איש קשר"
            />
          </div>

          {/* Email and Phone in a row */}
          <div className={modalStyles.formRow}>
            <div className={modalStyles.field}>
              <label className={modalStyles.fieldLabel}>
                אימייל
                <span className={modalStyles.fieldOptional}>(אופציונלי)</span>
              </label>
              <input
                type="email"
                name="contact_email"
                value={formData.contact_email}
                onChange={handleChange}
                className={`${modalStyles.fieldInput} ${errors.contact_email ? modalStyles.error : ''}`}
                placeholder="email@example.com"
              />
              {errors.contact_email && (
                <div className={modalStyles.fieldError}>{errors.contact_email}</div>
              )}
            </div>

            <div className={modalStyles.field}>
              <label className={modalStyles.fieldLabel}>
                טלפון
                <span className={modalStyles.fieldOptional}>(אופציונלי)</span>
              </label>
              <input
                type="tel"
                name="contact_phone"
                value={formData.contact_phone}
                onChange={handleChange}
                className={modalStyles.fieldInput}
                placeholder="050-000-0000"
              />
            </div>
          </div>

          {/* Website - Optional */}
          <div className={modalStyles.field}>
            <label className={modalStyles.fieldLabel}>
              אתר אינטרנט
              <span className={modalStyles.fieldOptional}>(אופציונלי)</span>
            </label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              className={modalStyles.fieldInput}
              placeholder="https://example.com"
            />
          </div>

          {/* Notes - Optional */}
          <div className={modalStyles.field}>
            <label className={modalStyles.fieldLabel}>
              הערות
              <span className={modalStyles.fieldOptional}>(אופציונלי)</span>
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className={modalStyles.fieldTextarea}
              rows={3}
              placeholder="הערות נוספות..."
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={modalStyles.footer}>
        <button type="button" className={modalStyles.cancelButton} onClick={onCancel}>
          ביטול
        </button>
        <button
          type="button"
          className={modalStyles.primaryButton}
          onClick={onSave}
          disabled={isSaving}
        >
          {isSaving ? 'שומר...' : 'שמור'}
        </button>
      </div>
    </>
  );
};

const VendorFormModal = ({
  open,
  onClose,
  projectItem,
  vendorTypeId,
  editVendorId, // For editing a standalone vendor
  onSubmitRef,
  isEditMode = false,
}) => {
  const { currentProject } = useApp();

  // Screen state: 'select' or 'edit'
  const [screen, setScreen] = useState('select');
  const [formData, setFormData] = useState(DEFAULT_VENDOR_VALUES);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [editingVendorId, setEditingVendorId] = useState(null); // Track which vendor we're editing
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Determine the mode: 'projectItem' (link vendor to item), 'vendorType' (assign vendor type), or 'editVendor' (edit standalone)
  const mode = projectItem
    ? 'projectItem'
    : vendorTypeId
      ? 'vendorType'
      : editVendorId
        ? 'editVendor'
        : null;

  // Centralized vendor creation hook (provides createVendor and vendorTypesGrouped)
  const { createVendor, vendorTypesGrouped } = useVendorCreate();

  // Get all vendors - RLS handles visibility (admin-generated + user's own + project members')
  const { data: allVendors } = useData(TABLES.MASTER_VENDORS, {
    select:
      'id, name, created_by, source, website, contact_person, contact_email, contact_phone, notes, vendor_type_id',
  });

  // Filter vendors by vendor_type_id when in vendorType mode
  const masterVendors = React.useMemo(() => {
    if (!allVendors) return [];
    // In vendorType mode, only show vendors that match the vendor type
    if (vendorTypeId) {
      return allVendors.filter(v => v.vendor_type_id === vendorTypeId);
    }
    return allVendors;
  }, [allVendors, vendorTypeId]);

  const { manageProjectVendor, updateMasterVendor } = useData(TABLES.PROJECT_VENDORS);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setErrors({});

      // If editVendorId is provided, go directly to edit screen with vendor data
      if (editVendorId && allVendors) {
        const vendor = allVendors.find(v => v.id === editVendorId);
        if (vendor) {
          setFormData({
            name: vendor.name || '',
            vendor_type_id: vendor.vendor_type_id || '',
            website: vendor.website || '',
            contact_person: vendor.contact_person || '',
            contact_email: vendor.contact_email || '',
            contact_phone: vendor.contact_phone || '',
            notes: vendor.notes || '',
          });
          setEditingVendorId(editVendorId);
          setScreen('edit');
          setSelectedVendorId(null);
          return;
        }
      }

      // Default: start at select screen
      setScreen('select');
      setFormData(DEFAULT_VENDOR_VALUES);
      setEditingVendorId(null);

      // Pre-select current vendor if editing
      if (isEditMode && projectItem?.vendor?.length > 0) {
        setSelectedVendorId(projectItem.vendor[0].master_vendor_id);
      } else {
        setSelectedVendorId(null);
      }
    }
  }, [open, isEditMode, projectItem, editVendorId, allVendors]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'נדרש שם ספק';
    if (!formData.vendor_type_id) newErrors.vendor_type_id = 'נדרש סוג ספק';
    if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = 'כתובת אימייל לא תקינה';
    }
    return newErrors;
  };

  // Handle "Edit" button click - go to edit screen with existing data
  const handleEditClick = () => {
    const vendor = masterVendors?.find(v => v.id === selectedVendorId);
    if (vendor) {
      setFormData({
        name: vendor.name || '',
        vendor_type_id: vendor.vendor_type_id || '',
        website: vendor.website || '',
        contact_person: vendor.contact_person || '',
        contact_email: vendor.contact_email || '',
        contact_phone: vendor.contact_phone || '',
        notes: vendor.notes || '',
      });
      setEditingVendorId(selectedVendorId);
      setScreen('edit');
    }
  };

  // Handle "Add New" button click - go to edit screen with empty form
  const handleAddNewClick = () => {
    // Pre-fill vendor_type_id if opening from vendor types tab
    setFormData({
      ...DEFAULT_VENDOR_VALUES,
      vendor_type_id: vendorTypeId || '',
    });
    setEditingVendorId(null);
    setScreen('edit');
  };

  // Handle save on edit screen
  const handleSaveVendor = async () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSaving(true);
    try {
      if (editingVendorId) {
        // Update existing vendor
        const result = await updateMasterVendor({
          p_master_vendor_id: editingVendorId,
          p_vendor_data: formData,
        });

        if (!result?.success) {
          setErrors({ submit: 'שגיאה בעדכון הספק' });
          return;
        }

        // In editVendor mode, close the modal after saving
        if (mode === 'editVendor') {
          onClose();
          return;
        }
      } else if (mode === 'projectItem') {
        // Create new vendor and link to project item
        const result = await manageProjectVendor({
          p_project_item_id: projectItem.id,
          p_vendor_data: formData,
          p_master_vendor_id: null,
        });

        if (!result?.success) {
          setErrors({ submit: 'שגיאה ביצירת הספק' });
          return;
        }

        // Select the newly created vendor
        setSelectedVendorId(result.vendor_id);
      } else if (mode === 'vendorType') {
        // Create new vendor with vendor_type_id using centralized hook
        const effectiveVendorTypeId = formData.vendor_type_id || vendorTypeId;
        const result = await createVendor({
          name: formData.name,
          vendor_type_id: effectiveVendorTypeId,
          website: formData.website,
          contact_person: formData.contact_person,
          contact_email: formData.contact_email,
          contact_phone: formData.contact_phone,
          notes: formData.notes,
          source: 'user-generated',
        });

        if (!result.success) {
          setErrors({ submit: result.error });
          return;
        }

        // Create vendor type assignment for this project
        const { error: assignmentError } = await supabase
          .from('project_vendor_type_assignments')
          .upsert(
            {
              project_id: currentProject.id,
              vendor_type_id: vendorTypeId,
              master_vendor_id: result.vendor.id,
              updated_at: new Date().toISOString(),
            },
            {
              onConflict: 'project_id,vendor_type_id',
            },
          );

        if (assignmentError) {
          console.warn('[VendorFormModal] Assignment error:', assignmentError);
          // Don't fail - vendor was created successfully
        }

        onClose();
        return;
      }

      // Go back to select screen
      setScreen('select');
      setErrors({});
    } catch (error) {
      console.error('[VendorFormModal] Save error:', error);
      setErrors({ submit: error.message });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel on edit screen - go back to select screen
  const handleCancelEdit = () => {
    // In editVendor mode, cancel should close the modal
    if (mode === 'editVendor') {
      onClose();
      return;
    }
    setScreen('select');
    setErrors({});
  };

  // Handle confirm on select screen - link vendor to project item OR assign vendor type
  const handleConfirmSelection = async () => {
    console.log('[VendorFormModal] handleConfirmSelection called', {
      selectedVendorId,
      projectItemId: projectItem?.id,
      vendorTypeId,
      mode,
    });
    if (!selectedVendorId) return;

    setIsSaving(true);
    try {
      if (mode === 'projectItem') {
        // Link vendor to project item
        console.log('[VendorFormModal] Calling manageProjectVendor...');
        const result = await manageProjectVendor({
          p_project_item_id: projectItem.id,
          p_vendor_data: {},
          p_master_vendor_id: selectedVendorId,
        });
        console.log('[VendorFormModal] manageProjectVendor result:', result);

        if (result?.success) {
          onClose();
        } else {
          setErrors({ submit: 'שגיאה בשמירת הספק' });
        }
      } else if (mode === 'vendorType') {
        // Insert vendor type assignment for this project
        const { error } = await supabase.from('project_vendor_type_assignments').upsert(
          {
            project_id: currentProject.id,
            vendor_type_id: vendorTypeId,
            master_vendor_id: selectedVendorId,
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: 'project_id,vendor_type_id',
          },
        );

        if (error) {
          console.error('[VendorFormModal] Assignment error:', error);
          setErrors({ submit: error.message });
          return;
        }

        onClose();
      }
    } catch (error) {
      console.error('[VendorFormModal] Confirm error:', error);
      setErrors({ submit: error.message });
    } finally {
      setIsSaving(false);
    }
  };

  // Set submit ref for keyboard shortcuts
  useEffect(() => {
    if (onSubmitRef) {
      onSubmitRef(() => {
        if (screen === 'select') {
          return handleConfirmSelection;
        } else {
          return handleSaveVendor;
        }
      });
    }
  }, [onSubmitRef, screen, selectedVendorId, formData]);

  if (!open) return null;

  return createPortal(
    <div className={modalStyles.overlay} onClick={onClose}>
      <div className={modalStyles.modal} dir="rtl" onClick={e => e.stopPropagation()}>
        {screen === 'select' ? (
          <SelectVendorScreen
            masterVendors={masterVendors}
            selectedVendorId={selectedVendorId}
            setSelectedVendorId={setSelectedVendorId}
            onEdit={handleEditClick}
            onAddNew={handleAddNewClick}
            onConfirm={handleConfirmSelection}
            onClose={onClose}
          />
        ) : (
          <EditVendorScreen
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            isEditing={!!editingVendorId}
            onSave={handleSaveVendor}
            onCancel={handleCancelEdit}
            isSaving={isSaving}
            vendorTypesGrouped={vendorTypesGrouped}
          />
        )}
      </div>
    </div>,
    document.body,
  );
};

export default VendorFormModal;
